import { SearchContextModel } from './search-context.model';
import { MonitoringKeywordGroupModel } from './monitoring-keyword-group.model';


/** This model is helpful for validating keyword groups */
export class ConflictingKeywordGroupValidationModel {
    word: string;
    contexts: SearchContextModel[] = [];
    /** True if term that is included in search results such as And/OR */
    includedSearchTerm: boolean;

    constructor (
        word: string,
        contexts: SearchContextModel[],
        includedSearchTerm: boolean
    ) {
        this.word = word;
        this.contexts = contexts;
        this.includedSearchTerm = includedSearchTerm;
    }


    static generateMapings(keywordGroups: MonitoringKeywordGroupModel[], caseSensitive: boolean) {
        const andOrKeywordsMap = new Map<string, ConflictingKeywordGroupValidationModel[]>();
        const notKeywordsMap = new Map<string, ConflictingKeywordGroupValidationModel[]>();

        // loop through all keyword groups and populate the andOrKeywordsMap & notKeywordsMap mappings
        // the key will be the keyword string and the value will be the constructed ConflictingKeywordGroupValidationModel object
        keywordGroups.forEach((keywordGroup, index) => {
            keywordGroup.and.forEach(andKeyword => {
                this.updateConflictingKeywordMap(andKeyword, keywordGroup.searchContexts, andOrKeywordsMap, caseSensitive, true);
            });

            keywordGroup.or.forEach(orKeyword => {
                this.updateConflictingKeywordMap(orKeyword, keywordGroup.searchContexts, andOrKeywordsMap, caseSensitive, true);
            });

            keywordGroup.not.forEach(notKeyword => {
                this.updateConflictingKeywordMap(notKeyword, keywordGroup.searchContexts, notKeywordsMap, caseSensitive, false);
            });
        });

        const result = {
            andOrMap: andOrKeywordsMap,
            notMap: notKeywordsMap
        };

        return result;
    }


    /**
     * Sets the keywordGroupMap param with the conflictingValidationModel for validating keywordGroups
     *
     * @param keyword - the key for the updated map
     * @param searchContexts - the context for which the keyword applies
     * @param keywordGroupMap - the map to be updated
     * @param caseSensitive - indicates if the search is case sensitive
     * @param index - position of the keyword group
     * @param includedSearchTerm - indicates if this is a term that is included or excluded from a search
     */
    static updateConflictingKeywordMap(
        keyword: string,
        searchContexts: SearchContextModel[],
        keywordGroupMap: Map<string, ConflictingKeywordGroupValidationModel[]>,
        caseSensitive: boolean,
        includedSearchTerm: boolean
    ) {
        let keywordKey = keyword;
        if (!caseSensitive) {
            keywordKey = keyword.toLowerCase();
        }

        const conflictingKeywordModel = new ConflictingKeywordGroupValidationModel(
            keywordKey,
            searchContexts,
            includedSearchTerm
        );

        const keywordsMapped = keywordGroupMap.get(keywordKey);
        if (keywordsMapped) {
            keywordsMapped.push(conflictingKeywordModel);
            keywordGroupMap.set(keywordKey, keywordsMapped);
        } else {
            keywordGroupMap.set(keywordKey, [conflictingKeywordModel]);
        }
    }

    /**
     * Returns boolean indicating if two models are in conflict
     *
     * @param otherWord - word to compare to this
     *
     * @returns true of a keyword is in conflict, false if not
     */
    isInConflict(otherWord: ConflictingKeywordGroupValidationModel): boolean {
        if (this.word !== otherWord.word) {
            // the words dont match
            return false;
        }

        if (this.includedSearchTerm === otherWord.includedSearchTerm) {
            // both are included/excluded, cant be conflicting
            return false;
        }

        let matchingContext;
        this.contexts.find(context => {
            matchingContext = otherWord.contexts.filter(otherContext => {
                return otherContext.value === context.value;
            });

            return !!matchingContext.length;
        });

        return !!matchingContext.length;
    }
}
